// Bileli · shared/auth — the Bileli session's identity marker, pure so it can be unit-tested headless.
//
// why this exists: the Bileli session (httpOnly cookies on api.bileli.kg) is SEPARATE from the Telegram
// session, and `GET /me` carries no Telegram id — so the client cannot tell, from the cookie alone,
// whether the Bileli session belongs to the Telegram account currently in the tab. We record which
// Telegram uid the session was minted for, and compare against the live Telegram user. A mismatch means
// the cookie is stale (a logout that never revoked it, or an account switch) and the session must be
// re-authed as the new account. Origin-wide localStorage on purpose: the cookie jar is origin-wide too.

export const AUTHED_TG_UID_KEY = 'bileli_authed_tg_uid';

export function getAuthedTelegramUid(): string | undefined {
  try {
    return localStorage.getItem(AUTHED_TG_UID_KEY) ?? undefined;
  } catch {
    return undefined; // private mode / storage disabled — treat as "unknown", never throw into auth
  }
}

export function rememberAuthedTelegramUid(uid: string): void {
  try {
    localStorage.setItem(AUTHED_TG_UID_KEY, uid);
  } catch { /* storage disabled — the identity check degrades to "can't compare", never crashes */ }
}

export function forgetAuthedTelegramUid(): void {
  try {
    localStorage.removeItem(AUTHED_TG_UID_KEY);
  } catch { /* ignore */ }
}

/**
 * Re-auth ONLY when we positively KNOW the Bileli session belongs to a DIFFERENT Telegram account.
 * Unknown on either side ⇒ false: never force a needless re-verify (each one spends the backend's
 * 5-sign-ins/10-min window), and a session with no recorded uid heals on the next logout instead.
 */
export function shouldReauthForIdentity(
  storedUid: string | undefined, liveUid: string | undefined,
): boolean {
  return storedUid != null && liveUid != null && storedUid !== liveUid;
}

/**
 * The Telegram user id inside a bot-signed initData. Parses a COPY — never touch the bytes sent to
 * /auth/telegram/verify (the HMAC is byte-exact). Undefined on any malformed input, by design.
 */
export function parseTelegramUidFromInitData(initData: string): string | undefined {
  try {
    const user = new URLSearchParams(initData).get('user');
    if (!user) return undefined;
    const id = (JSON.parse(user) as { id?: number | string }).id;
    return id != null ? String(id) : undefined;
  } catch {
    return undefined;
  }
}
